// Task Detail Modal - Shows full task summary with evidences
import { useState, useEffect } from 'react'
import axios from 'axios'
import { format, parseISO, isPast } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { TaskEvidencesLoader } from './TaskEvidences'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { 
  Calendar, Clock, Flame, Minus, ArrowDown, Ban, 
  CheckCircle2, AlertTriangle, Play, Edit, X, Users, Share2
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Urgency configuration
const URGENCY_CONFIG = {
  CRITICO: { icon: Flame, color: 'text-rose-500', bgColor: 'bg-rose-500/10', label: 'Crítico' },
  ALTO: { icon: AlertTriangle, color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'Alto' },
  MEDIO: { icon: Minus, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Medio' },
  BAJO: { icon: ArrowDown, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Bajo' },
  NINGUNO: { icon: Ban, color: 'text-gray-400', bgColor: 'bg-gray-500/10', label: 'Ninguno' }
}

// Status configuration
const STATUS_CONFIG = {
  'Pendiente': { icon: Clock, color: 'bg-amber-500', textColor: 'text-amber-500' },
  'En progreso': { icon: Play, color: 'bg-blue-500', textColor: 'text-blue-500' },
  'Bloqueado': { icon: Ban, color: 'bg-rose-500', textColor: 'text-rose-500' },
  'Terminado': { icon: CheckCircle2, color: 'bg-emerald-500', textColor: 'text-emerald-500' },
  'Vencida': { icon: AlertTriangle, color: 'bg-rose-600', textColor: 'text-rose-600' }
}

export function TaskDetailModal({ task, open, onOpenChange, onEdit, onStatusChange, isShared = false }) {
  const { locale } = useLanguage()
  const dateLocale = locale === 'es-MX' ? es : enUS
  const lang = locale === 'es-MX' ? 'es' : 'en'
  
  const [currentStatus, setCurrentStatus] = useState(task?.status || 'Pendiente')
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (task) {
      setCurrentStatus(task.status)
    }
  }, [task])

  if (!task) return null

  const dueDate = task.due_date ? parseISO(task.due_date) : null
  const isOverdue = dueDate && isPast(dueDate) && task.status !== 'Terminado'
  const urgencyConfig = URGENCY_CONFIG[task.urgencia] || URGENCY_CONFIG.MEDIO
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG['Pendiente']
  const UrgencyIcon = urgencyConfig.icon
  const StatusIcon = statusConfig.icon

  const handleStatusChange = async (newStatus) => {
    setUpdating(true)
    try {
      if (isShared && task.share_id) {
        // Update shared task
        await axios.put(`${API}/tasks/shared/${task.id}`, {
          share_id: task.share_id,
          status: newStatus
        })
      } else {
        // Update own task
        await axios.put(`${API}/tasks/${task.id}`, { status: newStatus })
      }
      
      setCurrentStatus(newStatus)
      toast.success(newStatus === 'Terminado' 
        ? (lang === 'es' ? 'Tarea completada' : 'Task completed')
        : (lang === 'es' ? 'Estado actualizado' : 'Status updated')
      )
      
      if (onStatusChange) {
        onStatusChange(task.id, newStatus)
      }
      
      // Trigger bonsai update if completed
      if (newStatus === 'Terminado') {
        window.dispatchEvent(new CustomEvent('bonsai-update'))
      }
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error(lang === 'es' ? 'Error al actualizar' : 'Update failed')
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">{task.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {lang === 'es' ? 'Detalle de la tarea' : 'Task details'}
        </DialogDescription>
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background border-b p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {isShared && (
                  <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                    <Users className="w-3 h-3 mr-1" />
                    {lang === 'es' ? 'Compartida' : 'Shared'}
                  </Badge>
                )}
                <Badge variant="outline" className={`text-xs ${urgencyConfig.bgColor} ${urgencyConfig.color} border-current`}>
                  <UrgencyIcon className="w-3 h-3 mr-1" />
                  {urgencyConfig.label}
                </Badge>
              </div>
              <h2 className="text-xl font-bold line-clamp-2">{task.title}</h2>
            </div>
            <button 
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-full hover:bg-secondary flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Status Section */}
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-secondary/50">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${statusConfig.color} flex items-center justify-center`}>
                <StatusIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {lang === 'es' ? 'Estado actual' : 'Current status'}
                </p>
                <p className="font-semibold">{currentStatus}</p>
              </div>
            </div>
            
            <Select
              value={currentStatus}
              onValueChange={handleStatusChange}
              disabled={updating}
            >
              <SelectTrigger className="w-auto h-10 px-4 rounded-full" data-testid="detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Pendiente">Pendiente</SelectItem>
                <SelectItem value="En progreso">En progreso</SelectItem>
                <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                <SelectItem value="Terminado">Terminado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          {dueDate && (
            <div className={`flex items-center gap-3 p-4 rounded-xl ${isOverdue ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-secondary/50'}`}>
              <Calendar className={`w-5 h-5 ${isOverdue ? 'text-rose-500' : 'text-muted-foreground'}`} />
              <div>
                <p className="text-sm text-muted-foreground">
                  {lang === 'es' ? 'Fecha límite' : 'Due date'}
                </p>
                <p className={`font-semibold ${isOverdue ? 'text-rose-500' : ''}`}>
                  {format(dueDate, "EEEE d 'de' MMMM, HH:mm", { locale: dateLocale })}
                </p>
                {isOverdue && (
                  <p className="text-xs text-rose-500 mt-1">
                    {lang === 'es' ? '¡Vencida!' : 'Overdue!'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {task.description && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                {lang === 'es' ? 'Descripción' : 'Description'}
              </h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap bg-secondary/30 p-3 rounded-lg">
                {task.description}
              </p>
            </div>
          )}

          {/* Owner info for shared tasks */}
          {isShared && task.owner_name && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {lang === 'es' ? 'Compartida por' : 'Shared by'}
                </p>
                <p className="text-sm font-medium">{task.owner_name}</p>
              </div>
            </div>
          )}

          {/* Evidences Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {lang === 'es' ? 'Evidencias' : 'Evidences'}
            </h3>
            <TaskEvidencesLoader 
              taskId={task.id} 
              readOnly={false}
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-background border-t p-4 flex gap-3">
          {currentStatus !== 'Terminado' && (
            <Button
              onClick={() => handleStatusChange('Terminado')}
              disabled={updating}
              className="flex-1 rounded-full h-12 bg-emerald-500 hover:bg-emerald-600"
              data-testid="complete-task-btn"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              {lang === 'es' ? 'Completar' : 'Complete'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false)
              if (onEdit) onEdit(task)
            }}
            className="flex-1 rounded-full h-12"
            data-testid="edit-task-btn"
          >
            <Edit className="w-5 h-5 mr-2" />
            {lang === 'es' ? 'Editar' : 'Edit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TaskDetailModal
